import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/AdminLayout';
import { exposeService } from '../../services/exposeService';
import { ramadanService } from '../../services/ramadanService';
import { eventService } from '../../services/eventService';
import { bibliothequeService } from '../../services/bibliothequeService';
import { audioService } from '../../services/audioService';

export const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    exposes: 0,
    ramadan: 0,
    events: 0,
    livres: 0,
    marabouts: 0,
    audios: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [exposes, ramadan, events, livres, marabouts, audios] = await Promise.all([
        exposeService.getAll().catch(() => ({ results: [] })),
        ramadanService.getAll().catch(() => ({ results: [] })),
        eventService.getAll().catch(() => ({ results: [] })),
        bibliothequeService.getLivres().catch(() => ({ results: [] })),
        bibliothequeService.getMarabouts(1, 1000).catch(() => ({ results: [] })),
        audioService.getAll().catch(() => ({ results: [] })),
      ]);

      setStats({
        exposes: (exposes.results || exposes).length,
        ramadan: (ramadan.results || ramadan).length,
        events: (events.results || events).length,
        livres: (livres.results || livres).length,
        marabouts: marabouts.results ? marabouts.results.length : 0,
        audios: (audios.results || audios).length,
      });
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Exposés', value: stats.exposes, icon: '📝', color: 'from-blue-500 to-blue-600', link: '/admin/exposes', bgIcon: '📝' },
    { label: 'Ramadan', value: stats.ramadan, icon: '🌙', color: 'from-purple-500 to-purple-600', link: '/admin/ramadan', bgIcon: '🌙' },
    { label: 'Événements', value: stats.events, icon: '📅', color: 'from-green-500 to-green-600', link: '/admin/events', bgIcon: '📅' },
    { label: 'Marabouts', value: stats.marabouts, icon: '📚', color: 'from-yellow-500 to-yellow-600', link: '/admin/bibliotheque', bgIcon: '📚' },
    { label: 'Livres', value: stats.livres, icon: '📖', color: 'from-indigo-500 to-indigo-600', link: '/admin/bibliotheque', bgIcon: '📖' },
    { label: 'Audios', value: stats.audios, icon: '🎵', color: 'from-pink-500 to-pink-600', link: '/admin/mediatheque', bgIcon: '🎵' },
  ];

  const quickActions = [
    { label: 'Gérer Exposés', icon: '📝', link: '/admin/exposes', color: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700' },
    { label: 'Gérer Ramadan', icon: '🌙', link: '/admin/ramadan', color: 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700' },
    { label: 'Gérer Événements', icon: '📅', link: '/admin/events', color: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' },
    { label: 'Gérer Bibliothèque', icon: '📚', link: '/admin/bibliotheque', color: 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700' },
    { label: 'Gérer Médiathèque', icon: '🎵', link: '/admin/mediatheque', color: 'bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700' },
    { label: 'Gérer Daara', icon: '📚', link: '/admin/daara', color: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' },
    { label: 'Messages Contact', icon: '📧', link: '/admin/contact', color: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700' },
    { label: 'Gérer Utilisateurs', icon: '👥', link: '/admin/users', color: 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700' },
    { label: 'Base de Connaissances', icon: '💬', link: '/admin/knowledge-base', color: 'bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700' },
    { label: 'Historique Chat', icon: '💭', link: '/admin/chat-history', color: 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700' },
  ];

  if (loading) {
    return (
      <AdminLayout activeSection="dashboard">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-700 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Chargement des statistiques...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Link
            key={stat.label}
            to={stat.link}
            className={`bg-gradient-to-br ${stat.color} rounded-2xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl relative overflow-hidden group`}
            style={{ 
              animation: `slideUp 0.5s ease-out`,
              animationDelay: `${index * 100}ms`,
              animationFillMode: 'both'
            }}
          >
            <div className="absolute top-0 right-0 text-8xl opacity-10 transform rotate-12 group-hover:rotate-0 group-hover:scale-125 transition-transform duration-500">
              {stat.bgIcon}
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-4xl">{stat.icon}</span>
                <span className="text-white text-opacity-60 text-sm font-medium uppercase tracking-wider">
                  {stat.label}
                </span>
              </div>
              <div className="flex items-baseline space-x-2">
                <p className="text-5xl font-bold">{stat.value}</p>
                <span className="text-white text-opacity-70 text-lg">éléments</span>
              </div>
              <div className="mt-4 flex items-center text-white text-opacity-80 text-sm group-hover:text-opacity-100 transition-opacity">
                <span>Voir les détails</span>
                <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div 
        className="bg-white rounded-2xl shadow-xl p-8 mb-6" 
        style={{ 
          animation: 'slideUp 0.5s ease-out',
          animationDelay: '600ms',
          animationFillMode: 'both'
        }}
      >
        <div className="flex items-center mb-6">
          <div className="bg-gradient-to-r from-green-700 to-green-900 text-white rounded-full p-3 mr-4">
            <span className="text-2xl">⚡</span>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-green-700">Actions rapides</h2>
            <p className="text-gray-600 text-sm">Accès direct aux modules de gestion</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={action.label}
              to={action.link}
              className={`${action.color} text-white p-6 rounded-xl flex items-center space-x-4 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group`}
            >
              <div className="bg-white bg-opacity-20 rounded-full p-3 group-hover:bg-opacity-30 transition-all">
                <span className="text-3xl">{action.icon}</span>
              </div>
              <span className="font-bold text-lg flex-1">{action.label}</span>
              <span className="text-2xl transform group-hover:translate-x-2 transition-transform">→</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div 
          className="text-white rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1" 
          style={{ 
            background: 'linear-gradient(to bottom right, #2E7D32, #1B5E20, #2E7D32)',
            animation: 'slideUp 0.5s ease-out',
            animationDelay: '700ms',
            animationFillMode: 'both'
          }}
        >
          <div className="flex items-center mb-4">
            <div className="bg-white bg-opacity-20 rounded-full p-4 mr-4">
              <span className="text-4xl">📖</span>
            </div>
            <h3 className="text-2xl font-bold">Documentation API</h3>
          </div>
          <p className="text-white text-opacity-80 mb-6 text-lg">
            Consultez la documentation complète de l'API avec Swagger pour tester tous les endpoints
          </p>
            <a
              href="http://127.0.0.1:8000/swagger/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white px-6 py-3 rounded-xl font-bold inline-flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg"
              style={{ color: '#2E7D32' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E8F5E9'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
            <span>Ouvrir Swagger</span>
            <span>→</span>
          </a>
        </div>

        <div 
          className="text-white rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1" 
          style={{ 
            background: 'linear-gradient(to bottom right, #3b82f6, #2563eb, #1d4ed8)',
            animation: 'slideUp 0.5s ease-out',
            animationDelay: '800ms',
            animationFillMode: 'both'
          }}
        >
          <div className="flex items-center mb-4">
            <div className="bg-white bg-opacity-20 rounded-full p-4 mr-4">
              <span className="text-4xl">⚙️</span>
            </div>
            <h3 className="text-2xl font-bold">Admin Django</h3>
          </div>
          <p className="text-white text-opacity-80 mb-6 text-lg">
            Accédez à l'interface d'administration Django complète pour une gestion avancée
          </p>
          <a
            href="http://127.0.0.1:8000/admin/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-blue-600 px-6 py-3 rounded-xl font-bold inline-flex items-center space-x-2 hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <span>Ouvrir Admin</span>
            <span>→</span>
          </a>
        </div>
      </div>
    </AdminLayout>
  );
};
